// src/App.jsx
import React from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// react-redux, @reduxjs/toolkit (via ./store/store) and
// @tanstack/react-query-devtools are not in package.json / node_modules —
// they don't build. Nothing in the app actually reads from the Redux store
// (no useSelector/useDispatch anywhere), so both are dropped rather than
// installed. If you want the store back, run:
//   npm install react-redux @reduxjs/toolkit @tanstack/react-query-devtools
// and restore the two imports + <Provider>/<ReactQueryDevtools> below.
import { AuthContextProvider } from "./context/AuthContext";
import DynamicRoutes from "./components/DynamicRoutes";
import CookieBanner from "./components/CookieBanner";

import { ConfigProvider, App as AntdApp, Empty } from "antd"; // ⬅️ add
import { NotifyBridge } from "./utils/notify";
import TermsGate from "./components/TermsGate";
import useAppTheme from "./hooks/useAppTheme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 300_000,
      retry: (failureCount, error) =>
        error?.response?.status === 401 ? false : failureCount < 3,
    },
  },
});

// Central AntD theme. Was previously an unconfigured <ConfigProvider> (AntD
// defaults everywhere), while the sidebar was hand-styled with its own
// accent color (#b89b6e) and its own font stack — so the app visually
// disagreed with itself. This ties every AntD component (buttons, inputs,
// tabs, links, focus rings) to the same accent and font as the sidebar.
const antdTheme = {
  token: {
    colorPrimary: "#b89b6e",
    colorError: "#c93c37", // the same red as our status chips; replaces the default bright AntD red on every danger button
    colorSuccess: "#1f8a4c", // same green / amber as the status chips
    colorWarning: "#c98a14",
    borderRadiusLG: 12,
    boxShadowSecondary: "0 14px 36px rgba(26, 26, 31, 0.14), 0 2px 8px rgba(26, 26, 31, 0.06)",
    borderRadius: 8,
    fontFamily: "'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14,
    controlHeight: 36,
    colorText: "#1a1a1f",
    colorTextSecondary: "#6b6b77",
    colorTextTertiary: "#8e8e9a",
    colorBorder: "#e0e0e6",
    colorBorderSecondary: "#ececf0",
    colorBgLayout: "#f5f5f7",
  },
  components: {
    Button: { fontWeight: 500, primaryShadow: "none", defaultShadow: "none", dangerShadow: "none" },
    Table: {
      headerBg: "#fafafb",
      headerColor: "#6b6b77",
      headerSplitColor: "transparent",
      borderColor: "#eeeef2",
      rowHoverBg: "#fafafb",
      cellPaddingBlock: 12,
      cellPaddingInline: 14,
      headerBorderRadius: 8,
    },
    Card: { headerBg: "transparent", borderRadiusLG: 12 },
    Tag: { defaultBg: "#f3f3f6", defaultColor: "#4a4a54" },
    Segmented: { trackBg: "#efeff3", itemSelectedBg: "#ffffff" },
    Modal: { borderRadiusLG: 14, titleFontSize: 16, headerBg: "#ffffff", contentBg: "#ffffff", footerBg: "#ffffff" },
    Drawer: { footerPaddingBlock: 12, footerPaddingInline: 20 },
    Alert: { borderRadiusLG: 10 },
  },
};

// Every table / list / select with nothing in it shows this, so pages never need their own "no data" wiring
const renderEmpty = (component) =>
  component === "Table" || component === "List" ? (
    <div className="hms-empty">Nothing to show yet</div>
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
  );

// One look for every toast: top right, newest first, at most 4 on screen, progress bar, pauses on hover
const NOTIFICATION_CONFIG = { placement: "topRight", maxCount: 4, showProgress: true, pauseOnHover: true, stack: { threshold: 3 } };
const MESSAGE_CONFIG = { maxCount: 2, top: 72 };

function App() {
  const { palette } = useAppTheme();
  const appAntdTheme = React.useMemo(
    () => ({ ...antdTheme, token: {
        ...antdTheme.token,
        colorPrimary: palette.primary,
        colorInfo: palette.primary, // removes the default AntD blue from links, info tags and alerts
        colorLink: palette.primary,
      } }),
    [palette.primary]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={appAntdTheme} drawer={{ closable: { placement: "end" } }} renderEmpty={renderEmpty}>
        <AntdApp notification={NOTIFICATION_CONFIG} message={MESSAGE_CONFIG}>
          <NotifyBridge />
          <BrowserRouter>
            <AuthContextProvider>
              <DynamicRoutes />
              <TermsGate />
              <CookieBanner />
            </AuthContextProvider>
          </BrowserRouter>
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
